import type { FlowStep } from "../core/contracts";

export type OreScenario = "original" | "fixed" | "histograms";

// Recorded comparisons from the same first-pass ORE decision, not final plan costs.
export const ORE_CASES = {
  original: {
    label: "Original costing",
    detail: "Fix 35365062 OFF · no histograms",
    base: 2580.339857,
    ore: 539857.348583,
    estimatedRows: 278273,
    actualRows: 36136,
    winner: "base",
  },
  fixed: {
    label: "Nested cost included",
    detail: "Fix 35365062 ON · same SQL and data",
    base: 549169.304174,
    ore: 539857.348583,
    estimatedRows: 278273,
    actualRows: 36136,
    winner: "ore",
  },
  histograms: {
    label: "Better statistics",
    detail: "Fix OFF · date and type histograms (P09)",
    base: 2579.441264,
    ore: 60192.26228,
    estimatedRows: 39599,
    actualRows: 36136,
    winner: "base",
  },
} as const;

export const ORE_MEASUREMENTS = {
  base: {
    employeeCalls: 482740,
    dateCalls: 400,
    starts: 36116,
    buffers: 118035,
    wallMs: 224.3505,
  },
  ore: {
    employeeCalls: 36337,
    dateCalls: 400,
    starts: 36116,
    buffers: 118039,
    wallMs: 67.572,
  },
  rows: 220,
} as const;

type Truth = "TRUE" | "FALSE" | "UNKNOWN";
// Three-valued logic for an illustrative single-row, two-predicate ORE example.
export function oreMembership(
  p: Truth,
  q: Truth,
): { first: boolean; second: boolean; original: boolean } {
  return {
    first: p === "TRUE",
    second: q === "TRUE" && p !== "TRUE",
    original: p === "TRUE" || q === "TRUE",
  };
}

const step = (
  id: string,
  label: string,
  summary: string,
  district: string,
  chain: string[],
  note: string,
  kind: "runtime" | "ghidra" | "modeled" = "runtime",
): FlowStep => ({
  id: `ore-${id}`,
  label,
  shortLabel: label,
  summary,
  district,
  functionChain: chain,
  event: `ore.${id}`,
  phase:
    district === "optimizer"
      ? "optimize"
      : district === "table"
        ? "execute"
        : "transform",
  action: district === "table" ? "row-source" : "transform",
  durationMs: 4600,
  evidence: {
    kind,
    label:
      kind === "runtime"
        ? "Recorded lab"
        : kind === "ghidra"
          ? "Binary analysis"
          : "Explanatory model",
    note,
  },
});

export const ORE_FLOW: readonly FlowStep[] = [
  step(
    "predicate",
    "One SQL. Two ways to qualify.",
    "An assignment qualifies through the delegation rules OR because it belongs to the current employee. Follow the highlighted predicate into its query block.",
    "transformer",
    ["query block", "OR", "AND", "correlated subquery"],
    "Schematic predicate tree of the unchanged 844-character laboratory statement. The visual SQL is explanatory, not its exact text.",
    "modeled",
  ),
  step(
    "validity",
    "Allowed does not mean chosen.",
    "Oracle checks whether expansion is eligible, creates a candidate and then costs it. This lab passed eligibility even when the expanded candidate lost.",
    "transformer",
    ["kkqctdrvORE", "kkqoreCheckQBValidity", "candidate state"],
    "10053 contains generated and costed ORE candidates. Passing validity does not prove that the final plan uses the transformation.",
  ),
  step(
    "branches",
    "A query block becomes two branches.",
    "The own-assignment branch can use an index. Delegation keeps a full scan. These are two row-source branches executed by the same server process.",
    "plan",
    ["OR expansion", "VW_ORE", "UNION-ALL"],
    "The selected laboratory ORE plan has VW_ORE_5643E788 and UNION-ALL. The delegation branch retains TABLE ACCESS FULL.",
  ),
  step(
    "guard",
    "The same row must not arrive twice.",
    "A row satisfying both predicates belongs to one branch. LNNVL also handles UNKNOWN: try the predicate truth values below.",
    "plan",
    ["own predicate P", "delegation Q AND LNNVL(P)", "UNION ALL"],
    "The expanded plan uses LNNVL. This interactive truth table is an explanatory model of the single-row P OR Q identity; it preserves source-row multiplicity.",
    "modeled",
  ),
  step(
    "cost",
    "Follow the cost into the nested AND.",
    "Select a recorded case. With the fix enabled, recursive traversal finds the nested subquery and includes its estimated work in the original candidate too.",
    "optimizer",
    [
      "kkoaccsqf",
      "kkostTravLogSQAux",
      "weighted subquery executions × unit cost",
    ],
    "Ghidra maps fix 35365062 to recursive AND/OR traversal. The 10053 trace independently records the missing cost being included with the fix ON.",
    "ghidra",
  ),
  step(
    "decision",
    "Compare candidates at the same stage.",
    "The winner follows the recorded first-pass costs. Better histograms improve estimates, but in this lab they do not repair the omitted cost in the original candidate.",
    "optimizer",
    ["kkqctCostTransfQB", "kkqctUpdBestCostStt", "best state"],
    "Original: 2,580 vs 539,857. Fixed: 549,169 vs 539,857. P09 histograms: 2,579 vs 60,192. The later final ORE plan cost of 804,725 is a different optimization stage.",
  ),
  step(
    "estimates",
    "An estimate is a prediction.",
    "Compare the diagnostic filter estimate with its measured rows. Histogram improvements and the nested-subquery costing fix address different problems.",
    "optimizer",
    ["column statistics", "filter selectivity", "estimated rows"],
    "Diagnostic aggregate: 278,273 estimated vs 36,136 actual; P09: 39,599 vs 36,136. These are filter rows, distinct from the 36,116 measured subquery starts.",
  ),
  step(
    "runtime",
    "Less function work. Almost the same I/O.",
    "Both plans returned the same 220-row multiset. ORE reduced EMPLOYEE_ID calls by 92.47%; subquery starts stayed the same. Explore the measured counters below.",
    "table",
    ["DBMS_HPROF", "ALLSTATS", "full-fetch benchmark"],
    "Recorded synthetic-lab comparison, 8 September 2026, Oracle 19.32 AArch64. HPROF counts one full execution per plan; timings come from a separate warmed benchmark. This is not a production speedup prediction.",
  ),
];
